import invert from 'lodash/invert';
import isEmpty from 'lodash/isEmpty';
import Moment from 'moment-timezone';
import { GameMemberTypeMap, GameTypeMap, GameFrequencyMap, DATE_FORMAT, GamesAccountStatuses } from 'constants/AdminConstants';
import { TRACKING_GAME, COMPETITION_GAME } from 'constants/LanguageConstants';
var gameOptions = {
    team_classic: {
        memberType: GameMemberTypeMap.TEAMS,
        gameType: GameTypeMap.CLASSIC,
        label: "Team ".concat(TRACKING_GAME)
    },
    player_classic: {
        memberType: GameMemberTypeMap.PLAYERS,
        gameType: GameTypeMap.CLASSIC,
        label: "Player ".concat(TRACKING_GAME)
    },
    team_free_for_all: {
        memberType: GameMemberTypeMap.TEAMS,
        gameType: GameTypeMap.FREE_FOR_ALL,
        label: "Team ".concat(COMPETITION_GAME)
    },
    player_free_for_all: {
        memberType: GameMemberTypeMap.PLAYERS,
        gameType: GameTypeMap.FREE_FOR_ALL,
        label: "Player ".concat(COMPETITION_GAME)
    }
};
var frequencies = {
    Daily: GameFrequencyMap.DAILY,
    Weekly: GameFrequencyMap.WEEKLY,
    Monthly: GameFrequencyMap.MONTHLY,
    Yearly: GameFrequencyMap.YEARLY,
    Custom: GameFrequencyMap.NOT_REPEATING
};
var invertFrequencies = invert(frequencies);
/* eslint-enable camelcase */
// BEGIN - Functions
export var getGameSettings = function (opts) { return gameOptions[opts]; };
export var getGameOptions = function (memberType, gameType) {
    var parsedMemberType = typeof memberType === 'string' ? parseInt(memberType, 10) : memberType;
    var parsedGameType = typeof gameType === 'string' ? parseInt(gameType, 10) : gameType;
    var matchingObject = Object.values(gameOptions)
        .find(function (optionsObject) {
        var memberMatch = optionsObject.memberType === parsedMemberType;
        var gameMatch = optionsObject.gameType === parsedGameType;
        return memberMatch && gameMatch;
    });
    return matchingObject ? matchingObject.label : '';
};
export var getFrequencyLabel = function (frequency) { return invertFrequencies[frequency]; };
export var humanize = function (str) {
    return str.split('_')
        .map(function (fragment) { return fragment.charAt(0).toUpperCase() + fragment.slice(1); })
        .join(' ');
};
// Ex: Player Classic
export var getGameTypeLabel = function (memberType, gameType) {
    return humanize(getGameOptions(memberType, gameType));
};
// Ex: Player Classic - Monthly
export var getGameTypeFrequencyLabel = function (memberType, gameType, frequency) {
    return "".concat(getGameTypeLabel(memberType, gameType), " - ").concat(getFrequencyLabel(frequency));
};
export var orMax = function (remaining, max) {
    return remaining % max;
};
export var addLeadingZero = function (number) {
    return number < 10 ? "0".concat(number) : "".concat(number);
};
export var buildTimeString = function (hour, minute, second) {
    if (hour === void 0) { hour = 0; }
    if (minute === void 0) { minute = 0; }
    if (second === void 0) { second = 0; }
    return "".concat(addLeadingZero(hour), ":").concat(addLeadingZero(minute), ":").concat(addLeadingZero(second));
};
export var timeRemainingString = function (startDate, endDate, startTime, endTime, timeZone) {
    if (!timeZone) {
        console.warn('timeRemainingString timeZone unset');
    }
    var guardedEndTime = endTime || '23:59:59';
    var end = Moment.tz("".concat(endDate, " ").concat(guardedEndTime), DATE_FORMAT, timeZone);
    var now = Moment.tz(timeZone);
    var endOfMonth;
    var days;
    var months = end.diff(now, 'months');
    if (months > 0) {
        endOfMonth = Moment.tz(timeZone).endOf('month');
        days = endOfMonth.diff(now, 'days');
    }
    else {
        days = end.diff(now, 'days');
    }
    var hours = orMax(end.diff(now, 'hours'), 24);
    var minutes = orMax(end.diff(now, 'minutes'), 60);
    var seconds = orMax(end.diff(now, 'seconds'), 60);
    var result;
    if (months > 0) {
        result = "".concat(months, "m ").concat(days, "d ").concat(hours, "h");
    }
    else if (days > 0) {
        result = "".concat(days, "d ").concat(hours, "h ").concat(minutes, "m");
    }
    else {
        result = "".concat(hours, "h ").concat(minutes, "m ").concat(seconds, "s");
    }
    // Sanity check
    var start = Moment.tz("".concat(startDate, " ").concat(startTime || '00:00:00'), DATE_FORMAT, timeZone).valueOf();
    var rightNow = Moment.tz(timeZone).valueOf();
    if (rightNow < start) {
        result = 'Game Not Started';
    }
    else if (rightNow > end.valueOf()) {
        result = 'Game Over';
    }
    return result;
};
export var timeRemainingStringFromGame = function (settings, timeZone) {
    var startDate = settings.start_date, endDate = settings.end_date, stHr = settings.start_hour, stMin = settings.start_minute, eHr = settings.end_hour, eMin = settings.end_minute;
    var startHour = parseInt(String(stHr), 10);
    var startMinute = parseInt(String(stMin), 10);
    var endHour = parseInt(String(eHr), 10);
    var endMinute = parseInt(String(eMin), 10);
    var startTime = (startHour > 0 || startMinute > 0) ?
        buildTimeString(startHour, startMinute) :
        undefined;
    var endTime = (endHour > 0 || endMinute > 0) ?
        buildTimeString(endHour, endMinute) :
        undefined;
    return timeRemainingString(startDate, endDate, startTime, endTime, timeZone);
};
export var timeRemainingPercentage = function (startDate, endDate, startTime, endTime, timeZone) {
    if (!timeZone) {
        console.warn('timeRemainingPercentage timeZone unset');
    }
    var guardedStartTime = startTime || '00:00:00';
    var guardedEndTime = endTime || '23:59:59';
    var start = Moment.tz("".concat(startDate, " ").concat(guardedStartTime), DATE_FORMAT, timeZone).valueOf();
    var end = Moment.tz("".concat(endDate, " ").concat(guardedEndTime), DATE_FORMAT, timeZone).valueOf();
    var now = Moment.tz(timeZone).valueOf();
    var total = end - start;
    var remaining = end - now;
    return now < start || now > end ?
        0 :
        Math.max(20, 100 - Math.floor((remaining / total) * 100));
};
export var timeRemainingPercentageFromGame = function (game, timeZone) {
    var startDate = game.start_date, endDate = game.end_date, stHr = game.start_hour, stMin = game.start_minute, eHr = game.end_hour, eMin = game.end_minute;
    var startHour = parseInt(String(stHr), 10);
    var startMinute = parseInt(String(stMin), 10);
    var endHour = parseInt(String(eHr), 10);
    var endMinute = parseInt(String(eMin), 10);
    var startTime = startHour > 0 || startMinute > 0 ?
        buildTimeString(startHour, startMinute) :
        undefined;
    var endTime = endHour > 0 || endMinute > 0 ?
        buildTimeString(endHour, endMinute) :
        undefined;
    return timeRemainingPercentage(startDate, endDate, startTime, endTime, timeZone);
};
export var getOwnerAccountId = function (_a) {
    var settings = _a.settings;
    return parseInt(String(settings.account_id), 10);
};
export var getOwnerAccountName = function (game) {
    var ownerAccountId = getOwnerAccountId(game);
    var ownerAccount = (Array.isArray(game.accounts) ? game.accounts : [])
        .find(function (account) { return parseInt(String(account.account_id), 10) === ownerAccountId; });
    return isEmpty(ownerAccount) ? '' : ownerAccount.name;
};
export var allTrackablesMappedForAccount = function (trackables, accountId) {
    var parsedAccountId = parseInt(String(accountId), 10);
    var guardedTrackables = Array.isArray(trackables) ? trackables : [];
    var trackableBelongsToAccount = function (info) { return parseInt(String(info.account_id), 10) === parsedAccountId; };
    return guardedTrackables
        .every(function (trackable) { return trackable.info.findIndex(trackableBelongsToAccount) > -1; });
};
export var getAccount = function (accounts, accountId) {
    var parsedAccountId = parseInt(String(accountId), 10);
    var guardedAccounts = Array.isArray(accounts) ? accounts : [];
    var matchedAccount = guardedAccounts
        .find(function (account) { return parseInt(String(account.account_id), 10) === parsedAccountId; });
    return isEmpty(matchedAccount) ? false : matchedAccount;
};
export var pendingInvitation = function (accounts, accountId) {
    var account = getAccount(accounts, accountId);
    return account !== false &&
        parseInt(String(account.games_account_status), 10) === GamesAccountStatuses.INVITED;
};
// END - Functions
